import { Fragment } from 'react';
import { ChevronRight } from 'lucide-react';
import UserAvatar from './UserAvatar';
import { dayGroupLabel, formatTz, relativeTime, roleBadgeStyle, roleLabel } from './auditConstants';

export interface LoginRow {
  id: number;
  createdAt: string;
  user: { id: number; name: string; email: string; role: string } | null;
  emailEntered: string;
  result: 'success' | 'failed' | 'blocked' | 'locked';
  reason: string | null;
  method: string;
  ip: string | null;
  location: { city: string | null; region: string | null; country: string | null; org: string | null };
  browser: string | null;
  os: string | null;
  deviceType: string | null;
  userAgent: string | null;
  sessionId: string | null;
  endedAt: string | null;
  isNewDevice: boolean;
  isNewIp: boolean;
  isUnusual: boolean;
  ipKnownCount: number;
  ipBlockedUntil: string | null;
  facilityName: string | null;
}

const RESULT_PILL: Record<LoginRow['result'], { bg: string; fg: string; border: string; pulse?: boolean }> = {
  success: { bg: 'rgba(16,185,129,0.15)', fg: '#10b981', border: 'rgba(16,185,129,0.25)' },
  failed: { bg: 'rgba(239,68,68,0.15)', fg: '#ef4444', border: 'rgba(239,68,68,0.3)' },
  blocked: { bg: 'rgba(239,68,68,0.25)', fg: '#ef4444', border: 'rgba(239,68,68,0.45)', pulse: true },
  locked: { bg: 'rgba(245,158,11,0.15)', fg: '#f59e0b', border: 'rgba(245,158,11,0.3)' },
};

function reasonLabel(row: LoginRow): string {
  if (row.result === 'success') {
    const ageMs = Date.now() - new Date(row.createdAt).getTime();
    return ageMs < 4 * 60 * 60 * 1000 ? 'Session active' : 'Signed in';
  }
  if (row.result === 'blocked') return 'IP blocked · 5 attempts';
  if (row.result === 'locked') return 'Account locked · 10 fails';
  if (row.reason === 'wrong_password') return 'Wrong password';
  if (row.reason === 'no_account') return 'No such account';
  return '—';
}

function flagPill(row: LoginRow): string | null {
  if (row.isUnusual) return 'Unusual';
  if (row.isNewDevice) return 'New device';
  if (row.isNewIp) return 'New IP';
  return null;
}

const GRID = '130px 1.5fr 1fr 1.25fr 1.25fr 110px 28px';

export default function LoginActivityTable({
  rows, loading, total, page, pageSize, onPageChange, onRowClick, selectedId,
}: {
  rows: LoginRow[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (p: number) => void;
  onRowClick: (row: LoginRow) => void;
  selectedId: number | null;
}) {
  let lastGroup: string | null = null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="audit-in" style={{ borderRadius: 14, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 12, padding: '11px 18px', borderBottom: '1px solid var(--border-subtle)' }}>
        {['Time', 'User', 'Result', 'IP / location', 'Device', 'Method', ''].map((h, i) => (
          <div key={i} style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</div>
        ))}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <div style={{ width: 22, height: 22, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.08)', borderTopColor: '#3b82f6', animation: 'spin 0.8s linear infinite', margin: '0 auto 10px' }} />
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading...</div>
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)', fontSize: 14 }}>
          No sign-in attempts match these filters.
        </div>
      )}

      {!loading && rows.map((row) => {
        const group = dayGroupLabel(row.createdAt);
        const showGroup = group !== lastGroup;
        lastGroup = group;
        const pill = RESULT_PILL[row.result];
        const flag = flagPill(row);
        const highlightRed = row.result === 'blocked' || (row.result === 'failed' && !row.user);
        const isSelected = selectedId === row.id;

        return (
          <Fragment key={row.id}>
            {showGroup && (
              <div style={{ padding: '8px 18px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid var(--border-subtle)' }}>
                {group}
              </div>
            )}
            <div
              className="audit-table-row"
              onClick={() => onRowClick(row)}
              style={{
                display: 'grid', gridTemplateColumns: GRID, gap: 12, padding: '11px 18px', alignItems: 'center',
                borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer',
                background: isSelected ? 'var(--bg-active)' : (highlightRed ? 'rgba(239,68,68,0.05)' : 'transparent'),
              }}
              onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = highlightRed ? 'rgba(239,68,68,0.05)' : 'transparent'; }}
            >
              {/* Time */}
              <div>
                <div style={{ fontFamily: "ui-monospace, 'Cascadia Mono', Consolas, monospace", fontSize: 13, color: 'var(--text-primary)' }}>
                  {formatTz(row.createdAt, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{relativeTime(row.createdAt)}</div>
              </div>

              {/* User */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <UserAvatar userId={row.user?.id ?? null} name={row.user?.name ?? null} size="table" />
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontWeight: 500, fontSize: 13, color: 'var(--text-primary)' }}>{row.user?.name ?? 'Unknown user'}</span>
                    {row.user && (
                      <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 99, fontWeight: 600, background: roleBadgeStyle(row.user.role).bg, color: roleBadgeStyle(row.user.role).color }}>
                        {roleLabel(row.user.role)}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.user ? row.user.email : `${row.emailEntered} (typed)`}
                  </div>
                </div>
              </div>

              {/* Result */}
              <div>
                <span className={pill.pulse ? 'audit-pulse' : undefined} style={{ fontSize: 10, padding: '2px 9px', borderRadius: 99, fontWeight: 600, border: `1px solid ${pill.border}`, background: pill.bg, color: pill.fg }}>
                  {row.result === 'blocked' ? 'Blocked' : row.result === 'locked' ? 'Locked out' : row.result === 'success' ? 'Success' : 'Failed'}
                </span>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{reasonLabel(row)}</div>
              </div>

              {/* IP / location */}
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontFamily: "ui-monospace, 'Cascadia Mono', Consolas, monospace", fontSize: 13, color: 'var(--text-primary)' }}>{row.ip ?? '—'}</span>
                  {flag && (
                    <span style={{ fontFamily: 'inherit', fontSize: 9, padding: '2px 7px', borderRadius: 99, fontWeight: 600, background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>{flag}</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.ip && !row.location.city && !row.location.country ? 'Internal network' : (
                    [row.location.city, row.location.region].filter(Boolean).join(', ') + (row.location.org ? ` · ${row.location.org}` : '') || '—'
                  )}
                </div>
              </div>

              {/* Device */}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.browser ?? '—'}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{[row.os, row.deviceType].filter(Boolean).join(' · ') || '—'}</div>
              </div>

              {/* Method */}
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {row.method === 'password' ? 'Password' : row.method}
              </div>

              <ChevronRight size={15} color="#6b7280" />
            </div>
          </Fragment>
        );
      })}

      <div style={{ padding: '10px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
        <span>Showing {rows.length} of {total} attempts · retained 365 days</span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>Prev</button>
          <span>{page} / {totalPages}</span>
          <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
}
